import { Download, Plus, RefreshCw, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import { useTheme } from "../../../../contexts/ThemeContext";
import { useToast } from "../../../../contexts/ToastContext";
import essentialInstrumentsService from "../../../../services/essentialInstrumentsService";
import { buildCSV, triggerCSVDownload } from "../../../../utils/csvExport";

const EssentialCurrenciesTab: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const toast = useToast();

  const [currencies, setCurrencies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add currency state
  const [newCurrency, setNewCurrency] = useState("");
  const [adding, setAdding] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCurrencies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await essentialInstrumentsService.getEssentialCurrencies();
      setCurrencies(response.currencies.sort());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch currencies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleAddCurrency = async () => {
    const trimmed = newCurrency.trim().toUpperCase();
    if (!trimmed) return;

    try {
      setAdding(true);
      const result = await essentialInstrumentsService.insertEssentialCurrencies([trimmed]);
      if (result.inserted > 0) {
        toast.success(`Currency "${trimmed}" added successfully`);
      } else {
        toast.info(`Currency "${trimmed}" already exists`);
      }
      setNewCurrency("");
      await fetchCurrencies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add currency");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteCurrency = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await essentialInstrumentsService.deleteEssentialCurrency(deleteTarget);
      toast.success(`Currency "${deleteTarget}" deleted successfully`);
      setDeleteTarget(null);
      await fetchCurrencies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete currency");
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddCurrency();
    }
  };

  const handleDownloadCSV = useCallback(() => {
    const headers = ["currencyCode"];
    const rows = currencies.map((c) => [c]);
    const csv = buildCSV(headers, rows);
    const filename = `essential_currencies_${new Date().toISOString().split("T")[0]}.csv`;
    triggerCSVDownload(csv, filename);
  }, [currencies]);

  if (loading && currencies.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            } mx-auto`}
          />
          <p className={`mt-3 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            Loading currencies...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add Currency Row */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          value={newCurrency}
          onChange={(e) => setNewCurrency(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter currency code (e.g. EUR)"
          className={`px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 w-64 ${
            isDarkMode
              ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey placeholder-rp-grey/40"
              : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900 placeholder-gray-500"
          }`}
        />
        <button
          onClick={handleAddCurrency}
          disabled={adding || !newCurrency.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {adding ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Add Currency
        </button>
        <button
          onClick={handleDownloadCSV}
          disabled={currencies.length === 0}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isDarkMode
              ? "border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <Download className="w-4 h-4" />
          Download CSV
        </button>
        <button
          onClick={fetchCurrencies}
          disabled={loading}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
            isDarkMode
              ? "border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
          }`}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
          {error}
        </div>
      )}

      {/* Currencies Table */}
      <div className={`border rounded-lg overflow-hidden ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
        <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
          <thead className={isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"}>
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Currency Code
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                }`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator bg-rp-surface" : "divide-gray-200 bg-white"}`}>
            {currencies.length === 0 ? (
              <tr>
                <td
                  colSpan={2}
                  className={`px-6 py-8 text-center text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                >
                  No essential currencies configured
                </td>
              </tr>
            ) : (
              currencies.map((currency) => (
                <tr key={currency} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                  <td className={`px-6 py-3 text-sm font-mono ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                    {currency}
                  </td>
                  <td className="px-6 py-3 text-sm">
                    <button
                      onClick={() => setDeleteTarget(currency)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
        Total: {currencies.length} currencies
      </div>

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCurrency}
        title="Delete Currency"
        message={`Are you sure you want to remove "${deleteTarget}" from essential currencies? This action cannot be undone.`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        loading={deleting}
        isDestructive={true}
      />
    </div>
  );
};

export default EssentialCurrenciesTab;
